import {
  Constants,
  getUkcAgilityActSeconds,
  getUkcAgilityComparisonTime,
  getUkcAgilityDivisionFromHeight,
  getUkcAgilitySctSeconds,
  isUkcAgilityDynamicClass,
  ukcRallyClassKey,
  ukcRallyClassLabel,
} from "@eagerdog/constants";
import { 
  IScore,
  IShow,
  IObedienceAward,
  IObedienceCombinedAward,
  IObedienceAwards,
  IRallyObedienceAward,
  IRallyObedienceAwards,
  IScentworkAwards,
  IScentworkHITAward,
  IScentworkHITElementTime,
  IScentworkFirstPlaceAward,
  IUkcObedienceAwards,
  IUkcObedienceAward,
  IUkcObedienceCombinedAward,
  IUkcRallyObedienceAwards,
  IUkcRallyObedienceAward,
  IUkcAgilityAwards,
  IUkcAgilityAwardWinner
} from "@eagerdog/interfaces";

const obedienceRegularLevels = [
  Constants.dog_class_element_level.novice_a,
  Constants.dog_class_element_level.novice_b,
  Constants.dog_class_element_level.open_a,
  Constants.dog_class_element_level.open_b,
  Constants.dog_class_element_level.utility_a,
  Constants.dog_class_element_level.utility_b,
];

const obediencePreferredLevels = [
  Constants.dog_class_element_level.preferred_novice,
  Constants.dog_class_element_level.preferred_open,
  Constants.dog_class_element_level.preferred_utility,
];

const rallyCombinedLevels = [
  Constants.dog_class_element_level.advanced_b,
  Constants.dog_class_element_level.excellent_b,
];

const rallyTripleLevels = [
  Constants.dog_class_element_level.advanced_b,
  Constants.dog_class_element_level.excellent_b,
  Constants.dog_class_element_level.master,
];

/**
 * Calculate AKC Obedience awards for a single show
 */
export function calculateObedienceAwards(
  scores: IScore[],
  showId: string,
  specialtyBreed?: string
): IObedienceAwards {
  // Filter scores for this show
  const showScores = scores.filter(
    (s) =>
      s.show_type === Constants.show_type.obedience &&
      s.show_id.toString() === showId.toString() &&
      s.obedience?.result_type === Constants.score_result_type.qualified
  );

  // Calculate regular awards
  const regularAwards = calculateObedienceAwardsForClasses(showScores, obedienceRegularLevels, obediencePreferredLevels);

  // Calculate specialty awards if applicable
  let specialtyAwards;
  if (specialtyBreed && specialtyBreed.length > 0) {
    const specialtyScores = showScores.filter((s) => s.breed === specialtyBreed);
    specialtyAwards = calculateObedienceAwardsForClasses(specialtyScores, obedienceRegularLevels, obediencePreferredLevels);
  }

  return {
    regular: regularAwards,
    specialty: specialtyAwards,
  };
}

function calculateObedienceAwardsForClasses(
  scores: IScore[],
  regularLevels: string[],
  preferredLevels: string[]
) {
  // Group scores by dog and level
  const dogScores = new Map<string, Record<string, any>>();

  scores.forEach((s) => {
    const dogId = s.dog_id.toString();
    const level = s.dog_class?.level || "";
    const deductions = s.obedience?.total_deductions_only
      ? s.obedience?.total_deductions ?? 0
      : (s.obedience?.element_deductions || []).reduce(
          (prev, current) =>
            prev +
            (isNaN(parseFloat(current.element_deductions))
              ? 0
              : parseFloat(current.element_deductions)),
          0
        );
    const score = 200 - deductions;

    if (!dogScores.has(dogId)) {
      dogScores.set(dogId, {
        registered_name: s.registered_name || "",
        breed: s.breed || "",
        arm_number: s.arm_number || "",
      });
    }

    dogScores.get(dogId)![level] = score;
  });

  // Calculate High Score in Regular Classes
  let highRegular: IObedienceAward | undefined;
  dogScores.forEach((levels) => {
    regularLevels.forEach((lvl) => {
      const score = levels[lvl];
      if (typeof score === "number") {
        if (!highRegular || score > highRegular.score) {
          highRegular = {
            registered_name: levels.registered_name,
            breed: levels.breed,
            score,
            arm_number: levels.arm_number,
          };
        }
      }
    });
  });

  // Calculate High Score in Preferred Classes
  let highPreferred: IObedienceAward | undefined;
  dogScores.forEach((levels) => {
    preferredLevels.forEach((lvl) => {
      const score = levels[lvl];
      if (typeof score === "number") {
        if (!highPreferred || score > highPreferred.score) {
          highPreferred = {
            registered_name: levels.registered_name,
            breed: levels.breed,
            score,
            arm_number: levels.arm_number,
          };
        }
      }
    });
  });

  // Calculate High Combined (Open B + Utility B)
  let highCombined: IObedienceCombinedAward | undefined;
  dogScores.forEach((levels) => {
    const openBScore = levels[Constants.dog_class_element_level.open_b];
    const utilityBScore = levels[Constants.dog_class_element_level.utility_b];

    // Both Open B and Utility B scores must exist
    if (typeof openBScore === "number" && typeof utilityBScore === "number") {
      const combinedScore = openBScore + utilityBScore;
      if (!highCombined || combinedScore > highCombined.combined_score) {
        highCombined = {
          registered_name: levels.registered_name,
          breed: levels.breed,
          score: combinedScore,
          combined_score: combinedScore,
          open_b_score: openBScore,
          utility_b_score: utilityBScore,
          arm_number: levels.arm_number,
        };
      }
    }
  });

  return {
    highRegular,
    highPreferred,
    highCombined,
  };
}

/**
 * Calculate AKC Rally Obedience awards for a single show
 */
export function calculateRallyObedienceAwards(
  scores: IScore[],
  showId: string,
  specialtyBreed?: string
): IRallyObedienceAwards {
  // Filter scores for this show
  const showScores = scores.filter(
    (s) =>
      s.show_type === Constants.show_type.rally_obedience &&
      s.show_id.toString() === showId.toString() &&
      s.rally_obedience?.result_type === Constants.score_result_type.qualified
  );

  // Calculate regular awards
  const regularAwards = calculateRallyObedienceAwardsForClasses(showScores);

  // Calculate specialty awards if applicable
  let specialtyAwards;
  if (specialtyBreed && specialtyBreed.length > 0) {
    const specialtyScores = showScores.filter((s) => s.breed === specialtyBreed);
    specialtyAwards = calculateRallyObedienceAwardsForClasses(specialtyScores);
  }

  return {
    highCombined: regularAwards.highCombined,
    highTriple: regularAwards.highTriple,
    specialtyHighCombined: specialtyAwards?.highCombined,
    specialtyHighTriple: specialtyAwards?.highTriple,
  };
}

function calculateRallyObedienceAwardsForClasses(scores: IScore[]) {
  // Group scores by dog and level
  const dogScores = new Map<string, Record<string, any>>();

  scores.forEach((s) => {
    const dogId = s.dog_id.toString();
    const level = s.dog_class?.level || "";
    const deductions = s.rally_obedience?.total_deductions_only
      ? s.rally_obedience?.total_deductions ?? 0
      : (s.rally_obedience?.element_deductions || []).reduce(
          (prev, current) =>
            prev +
            (isNaN(parseFloat(current.element_deductions))
              ? 0
              : parseFloat(current.element_deductions)),
          0
        );
    const score = 100 - deductions;
    const time = s.rally_obedience?.time || 0;

    if (!dogScores.has(dogId)) {
      dogScores.set(dogId, {
        registered_name: s.registered_name || "",
        breed: s.breed || "",
        arm_number: s.arm_number || "",
      });
    }

    dogScores.get(dogId)![level] = score;
    dogScores.get(dogId)![`${level}_time`] = time;
  });

  // Calculate High Combined (Advanced B + Excellent B)
  let highCombined: IRallyObedienceAward | undefined;
  dogScores.forEach((levels) => {
    if (rallyCombinedLevels.every((lvl) => typeof levels[lvl] === "number")) {
      const total = rallyCombinedLevels.reduce((sum, lvl) => sum + (levels[lvl] as number), 0);
      const totalTime = rallyCombinedLevels.reduce((sum, lvl) => sum + (levels[`${lvl}_time`] as number || 0), 0);

      if (!highCombined || total > highCombined.total || (total === highCombined.total && totalTime < highCombined.totalTime)) {
        highCombined = {
          registered_name: levels.registered_name,
          breed: levels.breed,
          total,
          totalTime,
          arm_number: levels.arm_number,
        };
      }
    }
  });

  // Calculate High Triple (Advanced B + Excellent B + Master)
  let highTriple: IRallyObedienceAward | undefined;
  dogScores.forEach((levels) => {
    if (rallyTripleLevels.every((lvl) => typeof levels[lvl] === "number")) {
      const total = rallyTripleLevels.reduce((sum, lvl) => sum + (levels[lvl] as number), 0);
      const totalTime = rallyTripleLevels.reduce((sum, lvl) => sum + (levels[`${lvl}_time`] as number || 0), 0);

      if (!highTriple || total > highTriple.total || (total === highTriple.total && totalTime < highTriple.totalTime)) {
        highTriple = {
          registered_name: levels.registered_name,
          breed: levels.breed,
          total,
          totalTime,
          arm_number: levels.arm_number,
        };
      }
    }
  });

  return {
    highCombined,
    highTriple,
  };
}

// =====================
// AKC SCENTWORK AWARDS
// =====================

export const calculateScentworkAwards = (
  scores: IScore[]
): IScentworkAwards => {
  const levels = [
    Constants.dog_class_element_level.novice_a,
    Constants.dog_class_element_level.novice_b,
    Constants.dog_class_element_level.advanced,
    Constants.dog_class_element_level.excellent,
    Constants.dog_class_element_level.master,
  ];

  const elements = [
    Constants.dog_class_element.scentwork_container,
    Constants.dog_class_element.scentwork_interior,
    Constants.dog_class_element.scentwork_exterior,
    Constants.dog_class_element.scentwork_buried,
  ];

  const hitWinners: IScentworkHITAward[] = [];
  const firstPlaceWinners: IScentworkFirstPlaceAward[] = [];

  // Helper to parse time string to seconds
  const parseTime = (timeStr: string): number => {
    const parts = timeStr.split(":");
    const mins = parseInt(parts[0] || "0", 10);
    const secs = parseInt(parts[1] || "0", 10);
    const ms = parseInt(parts[2] || "0", 10);
    return (mins * 60) + secs + (ms / 100);
  };

  // Qualified + time for one element score (multi-search levels must Q in every search).
  const evaluateElementScore = (score: IScore, level: string, element: string): { qualified: boolean; time: number } => {
    const isMaster = level === Constants.dog_class_element_level.scentwork_master ||
                     level === Constants.dog_class_element_level.master;
    const isExcellent = level === Constants.dog_class_element_level.scentwork_excellent ||
                        level === Constants.dog_class_element_level.excellent;
    const isInterior = element === Constants.dog_class_element.scentwork_interior;
    const usesMasterArray = isMaster || (isExcellent && isInterior);

    if (usesMasterArray && score.scentwork_master && Array.isArray(score.scentwork_master) && score.scentwork_master.length > 0) {
      const qualified = score.scentwork_master.every(search =>
        search?.result_type === Constants.score_result_type.qualified
      );
      const time = score.scentwork_master.reduce((sum, search) => sum + parseTime(search?.search_time || "00:00:00"), 0);
      return { qualified, time };
    }

    if (score.scentwork) {
      return {
        qualified: score.scentwork.result_type === Constants.score_result_type.qualified,
        time: parseTime(score.scentwork.search_time || "00:00:00"),
      };
    }

    return { qualified: false, time: 0 };
  };

  // ---- High in Trial ----
  // One HIT per level (Novice A & B are divisions of one level and compete
  // together). A trial doesn't have to offer all four elements, so HIT is
  // judged on the elements that ARE offered at that level in this trial: the
  // dog must qualify in every one of them; fastest combined time wins.
  const hitLevelGroups: Array<{ label: string; levels: string[] }> = [
    { label: Constants.dog_class_element_level.novice, levels: [Constants.dog_class_element_level.novice_a, Constants.dog_class_element_level.novice_b] },
    { label: Constants.dog_class_element_level.advanced, levels: [Constants.dog_class_element_level.advanced] },
    { label: Constants.dog_class_element_level.excellent, levels: [Constants.dog_class_element_level.excellent] },
    { label: Constants.dog_class_element_level.master, levels: [Constants.dog_class_element_level.master] },
  ];

  const hitElements = elements.filter(element =>
    scores.some(s => s.dog_class?.show_element === element)
  );

  for (const group of hitLevelGroups) {
    const levelScores = scores.filter(s => group.levels.includes(s.dog_class?.level || ""));
    if (levelScores.length === 0) continue;

    // Elements with entries at this level in this trial, in standard order
    const requiredElements = elements.filter(element =>
      levelScores.some(s => s.dog_class?.show_element === element)
    );
    if (requiredElements.length === 0) continue;

    // Group scores by dog
    const dogScoresMap = new Map<string, IScore[]>();
    levelScores.forEach(score => {
      const dogId = score.dog_id?.toString() || '';
      if (!dogScoresMap.has(dogId)) {
        dogScoresMap.set(dogId, []);
      }
      dogScoresMap.get(dogId)!.push(score);
    });

    const candidates: Array<{
      arm_number: string;
      call_name: string;
      breed: string;
      owner_name: string;
      elements: IScentworkHITElementTime[];
      combinedTime: number;
    }> = [];

    dogScoresMap.forEach((dogScores) => {
      const elementTimes: IScentworkHITElementTime[] = [];

      for (const element of requiredElements) {
        const elementScore = dogScores.find(s => s.dog_class?.show_element === element);
        if (!elementScore) {
          return; // not entered in an offered element
        }

        const { qualified, time } = evaluateElementScore(elementScore, elementScore.dog_class?.level || "", element);
        if (!qualified) {
          return;
        }

        elementTimes.push({ element, time });
      }

      const firstScore = dogScores[0];
      candidates.push({
        arm_number: firstScore.arm_number || '',
        call_name: firstScore.call_name || '',
        breed: firstScore.breed || '',
        owner_name: firstScore.owner_name || '',
        elements: elementTimes,
        combinedTime: elementTimes.reduce((sum, e) => sum + e.time, 0),
      });
    });

    if (candidates.length > 0) {
      candidates.sort((a, b) => a.combinedTime - b.combinedTime);
      const winner = candidates[0];
      const timeFor = (element: string) => winner.elements.find(e => e.element === element)?.time || 0;

      hitWinners.push({
        level: group.label,
        arm_number: winner.arm_number,
        call_name: winner.call_name,
        breed: winner.breed,
        owner_name: winner.owner_name,
        elements: winner.elements,
        container_time: timeFor(Constants.dog_class_element.scentwork_container),
        interior_time: timeFor(Constants.dog_class_element.scentwork_interior),
        exterior_time: timeFor(Constants.dog_class_element.scentwork_exterior),
        buried_time: timeFor(Constants.dog_class_element.scentwork_buried),
        combined_time: winner.combinedTime,
      });
    }
  }

  // Calculate First Place for each element-level combination
  for (const element of elements) {
    for (const level of levels) {
      const elementLevelScores = scores.filter(s => 
        s.dog_class?.show_element === element &&
        s.dog_class?.level === level
      );

      if (elementLevelScores.length === 0) continue;

      // Sort by time to find first place
      const isMaster = level === Constants.dog_class_element_level.scentwork_master ||
                       level === Constants.dog_class_element_level.master;
      const isExcellent = level === Constants.dog_class_element_level.scentwork_excellent ||
                          level === Constants.dog_class_element_level.excellent;
      const isInterior = element === Constants.dog_class_element.scentwork_interior;
      const usesMasterArray = isMaster || (isExcellent && isInterior);

      const scoredDogs = elementLevelScores.map(score => {
        let timeInSeconds = 0;
        let isQualified = false;

        if (usesMasterArray && score.scentwork_master && Array.isArray(score.scentwork_master)) {
          // Multi-search: use cumulative time, check if all qualified
          isQualified = score.scentwork_master.every(search => 
            search.result_type === Constants.score_result_type.qualified
          );
          
          if (isQualified) {
            timeInSeconds = score.scentwork_master.reduce((sum, search) => {
              return sum + parseTime(search.search_time || "00:00:00");
            }, 0);
          }
        } else if (score.scentwork) {
          isQualified = score.scentwork.result_type === Constants.score_result_type.qualified;
          if (isQualified) {
            timeInSeconds = parseTime(score.scentwork.search_time || "00:00:00");
          }
        }

        return {
          score,
          time: timeInSeconds,
          isQualified,
        };
      }).filter(dog => dog.isQualified); // Only include qualified dogs

      // Sort by time (fastest first)
      scoredDogs.sort((a, b) => a.time - b.time);
      
      const firstPlace = scoredDogs[0];
      if (firstPlace) {
        firstPlaceWinners.push({
          element,
          level,
          arm_number: firstPlace.score.arm_number || '',
          call_name: firstPlace.score.call_name || '',
          breed: firstPlace.score.breed || '',
          owner_name: firstPlace.score.owner_name || '',
          time: firstPlace.time,
        });
      }
    }
  }

  // Calculate First Place for Detective class (separate from standard elements —
  // Detective does not factor into High in Trial per AKC rules).
  const detectiveScores = scores.filter(
    s =>
      s.dog_class?.show_element === Constants.dog_class_element.scentwork_detective &&
      s.dog_class?.level === Constants.dog_class_element_level.scentwork_detective
  );

  if (detectiveScores.length > 0) {
    const scoredDetective = detectiveScores
      .map(score => {
        let timeInSeconds = 0;
        let isQualified = false;

        if (score.scentwork) {
          isQualified = score.scentwork.result_type === Constants.score_result_type.qualified;
          if (isQualified) {
            timeInSeconds = parseTime(score.scentwork.search_time || "00:00:00");
          }
        }

        return { score, time: timeInSeconds, isQualified };
      })
      .filter(d => d.isQualified);

    scoredDetective.sort((a, b) => a.time - b.time);

    const detectiveFirst = scoredDetective[0];
    if (detectiveFirst) {
      firstPlaceWinners.push({
        element: Constants.dog_class_element.scentwork_detective,
        level: Constants.dog_class_element_level.scentwork_detective,
        arm_number: detectiveFirst.score.arm_number || '',
        call_name: detectiveFirst.score.call_name || '',
        breed: detectiveFirst.score.breed || '',
        owner_name: detectiveFirst.score.owner_name || '',
        time: detectiveFirst.time,
      });
    }
  }

  return {
    highInTrial: hitWinners,
    hitElements,
    firstPlace: firstPlaceWinners,
  };
};

// =======================
// UKC OBEDIENCE AWARDS
// =======================

export const calculateUkcObedienceAwards = (
  scores: IScore[]
): IUkcObedienceAwards => {
  const awardsData: IUkcObedienceAwards = {};

  // Helper to calculate score from deductions
  const calculateScore = (score: IScore): number | null => {
    const results = score.obedience;
    if (!results) return null;

    let totalScore: number;
    if (results.total_deductions_only) {
      totalScore = 200 - (results.total_deductions ?? Number.POSITIVE_INFINITY);
    } else {
      const deductionsSum = results.element_deductions?.reduce((sum, element) => {
        const parsed = parseFloat(element.element_deductions);
        return sum + (isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed);
      }, 0) || 0;
      totalScore = 200 - deductionsSum - (results.extra_deductions ?? 0);
    }

    return totalScore;
  };

  // Helper to find 1st place winners from each class/level
  const findFirstPlaceWinners = (eligibleScores: IScore[]): IScore[] => {
    // Group scores by level
    const groupedByLevel: Record<string, IScore[]> = {};
    
    eligibleScores.forEach(s => {
      const level = s.dog_class?.level ?? "unknown";
      if (!groupedByLevel[level]) {
        groupedByLevel[level] = [];
      }
      groupedByLevel[level].push(s);
    });

    // Find 1st place in each level (highest score)
    const firstPlaceWinners: IScore[] = [];
    
    Object.values(groupedByLevel).forEach(levelScores => {
      if (levelScores.length === 0) return;
      
      // Sort by score descending
      const sorted = levelScores
        .map(s => ({ score: s, calculated: calculateScore(s) }))
        .filter(s => s.calculated !== null)
        .sort((a, b) => (b.calculated ?? 0) - (a.calculated ?? 0));
      
      if (sorted.length > 0) {
        firstPlaceWinners.push(sorted[0].score);
      }
    });

    return firstPlaceWinners;
  };

  // Eligible levels for High in Trial
  const eligibleLevels = [
    Constants.dog_class_element_level.novice_a,
    Constants.dog_class_element_level.novice_b,
    Constants.dog_class_element_level.novice_c,
    Constants.dog_class_element_level.open_a,
    Constants.dog_class_element_level.open_b,
    Constants.dog_class_element_level.utility_a,
    Constants.dog_class_element_level.utility_b,
  ];

  // Filter qualified scores
  const qualifiedScores = scores.filter(
    s => s.obedience?.result_type === Constants.score_result_type.qualified
  );

  // High in Trial (all handlers including juniors are eligible)
  // UKC Rules:
  // 1. Only 1st place winners from eligible classes compete for HIT
  // 2. Must have score >= 195 to compete
  // 3. If only one dog >= 195, that dog wins
  // 4. If no dogs >= 195, highest scoring 1st place winner wins
  // 5. Ties below 195 require runoff (Section 2 procedure)
  // 6. A junior handler CAN win both HIT and High Scoring Junior
  const eligibleForHIT = qualifiedScores.filter(
    s => eligibleLevels.includes(s.dog_class?.level ?? "")
  );

  if (eligibleForHIT.length > 0) {
    // Step 1: Get only the 1st place winners from each class
    const firstPlaceWinners = findFirstPlaceWinners(eligibleForHIT);

    if (firstPlaceWinners.length > 0) {
      // Step 2: Calculate scores for all 1st place winners
      const scoredWinners = firstPlaceWinners
        .map(s => ({
          score: s,
          calculated: calculateScore(s),
        }))
        .filter(s => s.calculated !== null);

      // Step 3: Filter those with score >= 195
      const above195 = scoredWinners.filter(s => (s.calculated ?? 0) >= 195);

      let hitWinner: { score: IScore; calculated: number | null } | null = null;

      if (above195.length >= 1) {
        // Standard case: highest among those >= 195
        above195.sort((a, b) => (b.calculated ?? 0) - (a.calculated ?? 0));
        hitWinner = above195[0];
      } else if (scoredWinners.length > 0) {
        // Fallback: no dogs >= 195, highest 1st place winner wins
        // Note: If tied, per rules a runoff is required (Section 2)
        scoredWinners.sort((a, b) => {
          const scoreDiff = (b.calculated ?? 0) - (a.calculated ?? 0);
          if (scoreDiff !== 0) return scoreDiff;
          // Tie-breaker: lower armband number
          return (parseInt(a.score.arm_number || "999") || 999) - (parseInt(b.score.arm_number || "999") || 999);
        });
        hitWinner = scoredWinners[0];
      }

      if (hitWinner && hitWinner.calculated) {
        const winner = hitWinner.score;
        awardsData.highInTrial = {
          level: winner.dog_class?.level || '',
          arm_number: winner.arm_number || '',
          registered_name: winner.registered_name || '',
          registration_number: winner.registration_number || '',
          breed: winner.breed || '',
          score: hitWinner.calculated,
        };
      }
    }
  }

  // High in Trial Junior
  const eligibleForHITJunior = qualifiedScores.filter(s => s.is_junior);

  if (eligibleForHITJunior.length > 0) {
    const scoredDogs = eligibleForHITJunior.map(s => ({
      score: s,
      calculated: calculateScore(s),
    })).filter(s => s.calculated !== null);

    scoredDogs.sort((a, b) => (b.calculated || 0) - (a.calculated || 0));

    if (scoredDogs.length > 0 && scoredDogs[0].calculated) {
      const winner = scoredDogs[0].score;
      awardsData.highInTrialJunior = {
        level: winner.dog_class?.level || '',
        arm_number: winner.arm_number || '',
        registered_name: winner.registered_name || '',
        registration_number: winner.registration_number || '',
        breed: winner.breed || '',
        score: scoredDogs[0].calculated,
      };
    }
  }

  // High Combined (Open B + Utility A or Utility B)
  // Group by arm number
  const dogScoresMap = new Map<string, IScore[]>();
  qualifiedScores.forEach(score => {
    const armNumber = score.arm_number || '';
    if (!dogScoresMap.has(armNumber)) {
      dogScoresMap.set(armNumber, []);
    }
    dogScoresMap.get(armNumber)!.push(score);
  });

  // Find dogs with Open B + (Utility A or Utility B)
  const combinedCandidates: Array<{
    armNumber: string;
    scores: IScore[];
    levels: string[];
    totalScore: number;
  }> = [];

  dogScoresMap.forEach((dogScores, armNumber) => {
    const levels = dogScores.map(s => s.dog_class?.level || '');
    
    // Must have Open B and at least one of Utility A or Utility B
    const hasOpenB = levels.includes(Constants.dog_class_element_level.open_b);
    const hasUtilityA = levels.includes(Constants.dog_class_element_level.utility_a);
    const hasUtilityB = levels.includes(Constants.dog_class_element_level.utility_b);

    if (hasOpenB && (hasUtilityA || hasUtilityB)) {
      const totalScore = dogScores.reduce((sum, score) => {
        return sum + (calculateScore(score) || 0);
      }, 0);

      combinedCandidates.push({
        armNumber,
        scores: dogScores,
        levels,
        totalScore,
      });
    }
  });

  if (combinedCandidates.length > 0) {
    combinedCandidates.sort((a, b) => b.totalScore - a.totalScore);
    const winner = combinedCandidates[0];
    const firstScore = winner.scores[0];

    awardsData.highCombined = {
      levels: winner.levels,
      arm_number: firstScore.arm_number || '',
      registered_name: firstScore.registered_name || '',
      registration_number: firstScore.registration_number || '',
      breed: firstScore.breed || '',
      combined_score: winner.totalScore,
    };
  }

  return awardsData;
};

// ============================
// UKC RALLY OBEDIENCE AWARDS
// ============================

export const calculateUkcRallyObedienceAwards = (
  scores: IScore[]
): IUkcRallyObedienceAwards => {
  const awardsData: IUkcRallyObedienceAwards = {};

  // Helper to calculate score from deductions
  const calculateScore = (score: IScore): { points: number; time: number } | null => {
    const results = score.rally_obedience;
    if (!results) return null;

    let totalScore: number;
    if (results.total_deductions_only) {
      totalScore = 100 - (results.total_deductions ?? Number.POSITIVE_INFINITY);
    } else {
      const deductionsSum = results.element_deductions?.reduce((sum, element) => {
        const parsed = parseFloat(element.element_deductions);
        return sum + (isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed);
      }, 0) || 0;
      totalScore = 100 - deductionsSum;
    }

    return {
      points: totalScore,
      time: results.time ?? Number.POSITIVE_INFINITY,
    };
  };

  // Helper to find 1st place winners from each class (level + section)
  const findFirstPlaceWinners = (eligibleScores: IScore[]): IScore[] => {
    // Group scores by class; legacy "RO1 B" and RO1 + section B land together
    const groupedByLevel: Record<string, IScore[]> = {};
    
    eligibleScores.forEach(s => {
      const level = s.dog_class ? ukcRallyClassKey(s.dog_class.level, s.dog_class.section) : "unknown";
      if (!groupedByLevel[level]) {
        groupedByLevel[level] = [];
      }
      groupedByLevel[level].push(s);
    });

    // Find 1st place in each level (highest score, then fastest time)
    const firstPlaceWinners: IScore[] = [];
    
    Object.values(groupedByLevel).forEach(levelScores => {
      if (levelScores.length === 0) return;
      
      // Sort by score descending, then by time ascending
      const sorted = levelScores
        .map(s => ({ score: s, calculated: calculateScore(s) }))
        .filter(s => s.calculated !== null)
        .sort((a, b) => {
          if (a.calculated && b.calculated) {
            if (b.calculated.points !== a.calculated.points) {
              return b.calculated.points - a.calculated.points;
            }
            return a.calculated.time - b.calculated.time;
          }
          return 0;
        });
      
      if (sorted.length > 0) {
        firstPlaceWinners.push(sorted[0].score);
      }
    });

    return firstPlaceWinners;
  };

  // Eligible classes for High in Trial (RO1 C is NOT eligible). Compared on the
  // "RO1 B" label so legacy and level + section scores both match.
  const eligibleLevels = [
    Constants.dog_class_element_level.ro1_a,
    Constants.dog_class_element_level.ro1_b,
    // RO1 C is NOT eligible for HIT
    Constants.dog_class_element_level.ro2_a,
    Constants.dog_class_element_level.ro2_b,
    Constants.dog_class_element_level.ro3_a,
    Constants.dog_class_element_level.ro3_b,
    Constants.dog_class_element_level.rom_a,
    Constants.dog_class_element_level.rom_b,
  ];

  // Filter qualified scores
  const qualifiedScores = scores.filter(
    s => s.rally_obedience?.result_type === Constants.score_result_type.qualified
  );

  // High in Trial (all handlers including juniors are eligible)
  // UKC Rally Rules:
  // 1. Only 1st place winners from eligible classes compete for HIT
  // 2. RO1C is NOT eligible
  // 3. Highest score wins, time is tiebreaker
  // 4. A junior handler CAN win both HIT and High in Trial Junior
  const eligibleForHIT = qualifiedScores.filter(
    s => eligibleLevels.includes(ukcRallyClassLabel(s.dog_class?.level, s.dog_class?.section))
  );

  if (eligibleForHIT.length > 0) {
    // Step 1: Get only the 1st place winners from each class
    const firstPlaceWinners = findFirstPlaceWinners(eligibleForHIT);

    if (firstPlaceWinners.length > 0) {
      // Step 2: Calculate scores for all 1st place winners
      const scoredWinners = firstPlaceWinners
        .map(s => ({
          score: s,
          calculated: calculateScore(s),
        }))
        .filter(s => s.calculated !== null);

      // Step 3: Sort by points (descending), then by time (ascending)
      scoredWinners.sort((a, b) => {
        if (a.calculated && b.calculated) {
          if (b.calculated.points !== a.calculated.points) {
            return b.calculated.points - a.calculated.points;
          }
          return a.calculated.time - b.calculated.time;
        }
        return 0;
      });

      if (scoredWinners.length > 0 && scoredWinners[0].calculated) {
        const winner = scoredWinners[0].score;
        awardsData.highInTrial = {
          level: winner.dog_class?.level || '',
          arm_number: winner.arm_number || '',
          registered_name: winner.registered_name || '',
          registration_number: winner.registration_number || '',
          breed: winner.breed || '',
          score: scoredWinners[0].calculated.points,
          time: scoredWinners[0].calculated.time,
          owner_name: winner.owner_name,
          handler_name: winner.agent_name,
        };
      }
    }
  }

  // High in Trial Junior
  const eligibleForHITJunior = qualifiedScores.filter(s => s.is_junior);

  if (eligibleForHITJunior.length > 0) {
    const scoredDogs = eligibleForHITJunior.map(s => ({
      score: s,
      calculated: calculateScore(s),
    })).filter(s => s.calculated !== null);

    // Sort by points (descending), then by time (ascending)
    scoredDogs.sort((a, b) => {
      if (a.calculated && b.calculated) {
        if (b.calculated.points !== a.calculated.points) {
          return b.calculated.points - a.calculated.points;
        }
        return a.calculated.time - b.calculated.time;
      }
      return 0;
    });

    if (scoredDogs.length > 0 && scoredDogs[0].calculated) {
      const winner = scoredDogs[0].score;
      awardsData.highInTrialJunior = {
        level: winner.dog_class?.level || '',
        arm_number: winner.arm_number || '',
        registered_name: winner.registered_name || '',
        registration_number: winner.registration_number || '',
        breed: winner.breed || '',
        score: scoredDogs[0].calculated.points,
        time: scoredDogs[0].calculated.time,
        junior_number: winner.junior_number,
      };
    }
  }

  return awardsData;
};

// ====================
// UKC AGILITY AWARDS
// ====================

export const calculateUkcAgilityAwards = (
  scores: IScore[],
  show: IShow
): IUkcAgilityAwards => {
  const awardsData: IUkcAgilityAwards = {};

  const eligibleLevels = new Set([
    Constants.dog_class_element_level.beginner_a,
    Constants.dog_class_element_level.beginner_b,
    Constants.dog_class_element_level.intermediate_a,
    Constants.dog_class_element_level.intermediate_b,
    Constants.dog_class_element_level.excellent,
  ]);

  const getAct = (score: IScore): number => {
    const sct = getUkcAgilitySctSeconds(show, score.dog_class?.level);
    const division = getUkcAgilityDivisionFromHeight(score.height_at_withers);
    return getUkcAgilityActSeconds(sct, division);
  };

  const toAwardWinner = (score: IScore, act: number): IUkcAgilityAwardWinner => {
    const runningTime = score.agility?.time ?? Number.POSITIVE_INFINITY;
    const comparisonTime = getUkcAgilityComparisonTime(
      score.agility?.time,
      score.jump_height_type
    );
    const points = score.agility?.score ?? Number.NEGATIVE_INFINITY;

    return {
      level: score.dog_class?.level || "",
      arm_number: score.arm_number || "",
      registered_name: score.registered_name || "",
      registration_number: score.registration_number || "",
      breed: score.breed || "",
      score: points,
      running_time: runningTime,
      normal_sct: act,
      time_under_sct: act - comparisonTime,
      is_junior: score.is_junior,
      junior_number: score.junior_number,
    };
  };

  const compareWinners = (a: IUkcAgilityAwardWinner, b: IUkcAgilityAwardWinner): number => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }

    // time_under_sct is ACT - comparison time (Minimum height adds 5 seconds).
    // When SCT is missing, that value is 0 - comparison time, so larger still wins.
    if (b.time_under_sct !== a.time_under_sct) {
      return b.time_under_sct - a.time_under_sct;
    }

    return 0;
  };

  const isSameRank = (a: IUkcAgilityAwardWinner, b: IUkcAgilityAwardWinner): boolean => {
    return a.score === b.score && a.time_under_sct === b.time_under_sct;
  };

  const calculateAward = (eligibleScores: IScore[]) => {
    const candidates = eligibleScores
      .filter((score) => {
        const level = score.dog_class?.level || "";
        return (
          score.agility?.result_type === Constants.score_result_type.qualified &&
          typeof score.agility?.score === "number" &&
          typeof score.agility?.time === "number" &&
          eligibleLevels.has(level) &&
          !isUkcAgilityDynamicClass(level)
        );
      })
      .map((score) => {
        return toAwardWinner(score, getAct(score));
      })
      .filter((winner): winner is IUkcAgilityAwardWinner => winner !== null);

    if (candidates.length === 0) {
      return undefined;
    }

    candidates.sort(compareWinners);
    const topWinner = candidates[0];
    const tiedWinners = candidates.filter((candidate) => isSameRank(candidate, topWinner));

    return {
      winners: tiedWinners,
      is_tie: tiedWinners.length > 1,
    };
  };

  const hitScores = scores.filter((score) => !score.is_junior);
  const highScoringJuniorScores = scores.filter((score) => score.is_junior);

  awardsData.highInTrial = calculateAward(hitScores);
  awardsData.highScoringJunior = calculateAward(highScoringJuniorScores);

  return awardsData;
};

